import { isShopSlug, PROFILE_WHATSAPP_GREETING, whatsappLinks, type PublicShop, type PublicShopItem } from "@fusiqo/vozqi-shared";

/**
 * What the shop's page computes for itself, as plain functions (spec
 * 2026-09-26 §6): ShopPage.tsx only draws. The profile's own rules -- its
 * parts, prices, map and the Worker's paths -- are shared with the phone
 * (`public-shop-view.ts`).
 */

/** `/t/<slug>` → the slug, when it has a slug's shape; anything else is null. */
export function slugFromPath(pathname: string): string | null {
  const slug = /^\/t\/([^/]+)\/?$/.exec(pathname)?.[1];
  return isShopSlug(slug) ? slug : null;
}

/** The WhatsApp link, with the profile's greeting; null without the business's number (no button). */
export function shopWhatsappHref(shop: PublicShop): string | null {
  if (shop.business.whatsapp === null) return null;
  return whatsappLinks(shop.business.whatsapp, PROFILE_WHATSAPP_GREETING).web;
}

/**
 * WHERE THE VISITOR WAS, for «atrás» from an item's page (Review Focus 4):
 * what was loaded -- the services above and the products -- the search, the
 * category and the scroll, saved on tapping a service or a card.
 */
export type ShopSnapshot = ProductsSnapshot & {
  slug: string;
  /** The services listed so far, «Ver más servicios» included. */
  services: PublicShopItem[];
  servicesCursor: string | null;
  /** Which page listed each service, by token: its photo is vouched for by that page. */
  servicesPageOf: Record<string, string>;
  scrollY: number;
  savedAt: number;
};

/** The products part's own share of the snapshot. */
export type ProductsSnapshot = {
  q: string;
  /** The sidebar's category; null is «Todo». */
  section: string | null;
  items: PublicShopItem[];
  /** Which page listed each card, by token: its cover is vouched for by that page. */
  pageOf: Record<string, string>;
  /** The result count the list last showed; null when no page had answered yet. */
  total: number | null;
  nextCursor: string | null;
};

export const SNAPSHOT_TTL_MS = 10 * 60 * 1000;
export const SNAPSHOT_KEY = "cuotas-shop-snapshot";

/** A saved snapshot for this shop, younger than ten minutes, well formed -- else null. */
export function readSnapshot(raw: string | null, slug: string, now: number): ShopSnapshot | null {
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const snapshot = parsed as Partial<ShopSnapshot>;
  if (
    snapshot.slug !== slug ||
    typeof snapshot.savedAt !== "number" ||
    now - snapshot.savedAt > SNAPSHOT_TTL_MS ||
    typeof snapshot.q !== "string" ||
    !(snapshot.section === null || typeof snapshot.section === "string") ||
    !Array.isArray(snapshot.items) ||
    typeof snapshot.pageOf !== "object" ||
    snapshot.pageOf === null ||
    !(snapshot.total === null || typeof snapshot.total === "number") ||
    typeof snapshot.scrollY !== "number" ||
    !(snapshot.nextCursor === null || typeof snapshot.nextCursor === "string") ||
    !Array.isArray(snapshot.services) ||
    !(snapshot.servicesCursor === null || typeof snapshot.servicesCursor === "string") ||
    typeof snapshot.servicesPageOf !== "object" ||
    snapshot.servicesPageOf === null
  ) {
    return null;
  }
  return snapshot as ShopSnapshot;
}
